// utils/scheduleHistory.js
import moment from 'moment';

/**
 * Schedule History Manager
 * Tracks all changes to crew schedules and allows point-in-time restoration
 */
class ScheduleHistoryManager {
  constructor() {
    this.historyKey = 'schedule_history';
    this.maxHistoryItems = 100; // Keep last 100 changes
    this.maxHistoryDays = 30; // Keep 30 days of history
  }

  /**
   * Record a schedule change
   * @param {Object} change - The change details
   * @returns {string} - Change ID
   */
  recordChange(change) {
    const changeRecord = {
      id: this.generateChangeId(),
      timestamp: new Date().toISOString(),
      type: change.type,
      description: change.description,
      user: change.user || 'system',
      affectedDate: change.affectedDate,
      changes: {
        before: change.before,
        after: change.after
      },
      metadata: {
        ...change.metadata,
        source: change.source || 'manual',
        impact: change.impact
      }
    };

    // Get existing history
    const history = this.getHistory();
    
    // Add new change
    history.unshift(changeRecord);
    
    // Cleanup old entries
    this.cleanupHistory(history);
    
    // Save to localStorage (in production, this would be an API call)
    this.saveHistory(history);
    
    return changeRecord.id;
  }

  /**
   * Record a batch of changes as a single transaction
   * @param {Array} changes - Array of changes
   * @param {Object} transactionMeta - Transaction metadata
   */
  recordTransaction(changes, transactionMeta) {
    const transactionId = this.generateChangeId();
    const transaction = {
      id: transactionId,
      timestamp: new Date().toISOString(),
      type: 'transaction',
      description: transactionMeta.description || 'Batch operation',
      user: transactionMeta.user || 'system',
      changes: changes.map(change => ({
        ...change,
        transactionId
      })),
      metadata: {
        ...transactionMeta,
        changeCount: changes.length
      }
    };

    const history = this.getHistory();
    history.unshift(transaction);
    this.cleanupHistory(history);
    this.saveHistory(history);
    
    return transactionId;
  }

  /**
   * Get full history or filtered by criteria
   * @param {Object} filters - Optional filters
   */
  getHistory(filters = {}) {
    const history = JSON.parse(localStorage.getItem(this.historyKey) || '[]');
    
    if (!filters || Object.keys(filters).length === 0) {
      return history;
    }

    return history.filter(item => {
      if (filters.startDate && moment(item.timestamp).isBefore(filters.startDate)) {
        return false;
      }
      if (filters.endDate && moment(item.timestamp).isAfter(filters.endDate)) {
        return false;
      }
      if (filters.type && item.type !== filters.type) {
        return false;
      }
      if (filters.user && item.user !== filters.user) {
        return false;
      }
      if (filters.affectedDate && item.affectedDate !== filters.affectedDate) {
        return false;
      }
      return true;
    });
  }

  /**
   * Get a specific change by ID
   */
  getChange(changeId) {
    const history = this.getHistory();
    return history.find(item => item.id === changeId);
  }

  /**
   * Get the state at a specific point in time
   * @param {Date} targetDate - The date/time to restore to
   * @param {Object} currentState - Current schedule state
   */
  getStateAtTime(targetDate, currentState) {
    const history = this.getHistory();
    const targetMoment = moment(targetDate);
    
    // Get all changes after the target date (in reverse chronological order)
    const changesAfterTarget = history.filter(item => 
      moment(item.timestamp).isAfter(targetMoment)
    );

    // Starting from current state, reverse each change
    let restoredState = JSON.parse(JSON.stringify(currentState));
    
    changesAfterTarget.forEach(change => {
      restoredState = this.reverseChange(restoredState, change);
    });

    return restoredState;
  }

  /**
   * Create a restore point (snapshot)
   */
  createRestorePoint(state, description) {
    const restorePoint = {
      id: this.generateChangeId(),
      timestamp: new Date().toISOString(),
      type: 'restore_point',
      description: description || 'Manual restore point',
      user: 'system',
      snapshot: JSON.parse(JSON.stringify(state)),
      metadata: {
        crewCount: state.crews?.length || 0,
        routeCount: state.weeklyRoutes?.length || 0,
        siteCount: state.sites?.length || 0
      }
    };

    const history = this.getHistory();
    history.unshift(restorePoint);
    this.cleanupHistory(history);
    this.saveHistory(history);
    
    return restorePoint.id;
  }

  /**
   * Restore to a specific change or restore point
   */
  restoreToPoint(changeId, currentState) {
    const change = this.getChange(changeId);
    if (!change) {
      throw new Error('Change not found');
    }

    if (change.type === 'restore_point') {
      // Direct restore from snapshot
      return change.snapshot;
    } else {
      // Restore by reversing changes
      return this.getStateAtTime(change.timestamp, currentState);
    }
  }

  /**
   * Reverse a single change
   */
  reverseChange(state, change) {
    const newState = JSON.parse(JSON.stringify(state));

    switch (change.type) {
      case 'job_transfer':
        // Reverse job transfer
        if (change.changes.before && change.changes.after) {
          // Move job back to original crew
          // This would need to be implemented based on your state structure
        }
        break;
        
      case 'crew_assignment':
        // Reverse crew assignment
        break;
        
      case 'route_optimization':
        // Restore previous route
        if (change.changes.before) {
          // Apply previous route state
        }
        break;
        
      case 'transaction':
        // Reverse each change in the transaction (in reverse order)
        if (change.changes) {
          [...change.changes].reverse().forEach(subChange => {
            newState = this.reverseChange(newState, subChange);
          });
        }
        break;
    }

    return newState;
  }

  /**
   * Generate a unique change ID
   */
  generateChangeId() {
    return `ch_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Clean up old history entries
   */
  cleanupHistory(history) {
    const cutoffDate = moment().subtract(this.maxHistoryDays, 'days');
    
    // Remove old entries
    const filtered = history.filter(item => 
      moment(item.timestamp).isAfter(cutoffDate)
    );

    // Keep only max items
    return filtered.slice(0, this.maxHistoryItems);
  }

  /**
   * Save history to storage
   */
  saveHistory(history) {
    localStorage.setItem(this.historyKey, JSON.stringify(history));
    // In production, this would also sync to server
  }

  /**
   * Get history statistics
   */
  getHistoryStats() {
    const history = this.getHistory();
    const last24h = moment().subtract(24, 'hours');
    const last7d = moment().subtract(7, 'days');

    return {
      totalChanges: history.length,
      changesLast24h: history.filter(item => 
        moment(item.timestamp).isAfter(last24h)
      ).length,
      changesLast7d: history.filter(item => 
        moment(item.timestamp).isAfter(last7d)
      ).length,
      changesByType: history.reduce((acc, item) => {
        acc[item.type] = (acc[item.type] || 0) + 1;
        return acc;
      }, {}),
      oldestChange: history[history.length - 1]?.timestamp,
      newestChange: history[0]?.timestamp
    };
  }

  /**
   * Export history as CSV
   */
  exportHistory(history = null) {
    const data = history || this.getHistory();
    const csv = [
      ['ID', 'Timestamp', 'Type', 'Description', 'User', 'Impact'].join(','),
      ...data.map(item => [
        item.id,
        item.timestamp,
        item.type,
        `"${item.description}"`,
        item.user,
        item.metadata?.impact || ''
      ].join(','))
    ].join('\n');

    return csv;
  }
}

// Export singleton instance
export const scheduleHistory = new ScheduleHistoryManager();

// Helper functions for common operations
export const recordJobTransfer = (fromCrew, toCrew, jobs, impact) => {
  return scheduleHistory.recordChange({
    type: 'job_transfer',
    description: `Transferred ${jobs.length} jobs from ${fromCrew.name} to ${toCrew.name}`,
    affectedDate: moment().format('YYYY-MM-DD'),
    before: {
      fromCrewJobs: fromCrew.jobs,
      toCrewJobs: toCrew.jobs
    },
    after: {
      fromCrewJobs: fromCrew.jobs.filter(j => !jobs.includes(j)),
      toCrewJobs: [...toCrew.jobs, ...jobs]
    },
    metadata: {
      jobCount: jobs.length,
      hoursTransferred: impact.hoursTransferred,
      fromCrewId: fromCrew._id,
      toCrewId: toCrew._id,
      impact: `${impact.hoursTransferred}h moved, ${impact.efficiencyImprovement}% efficiency gain`
    }
  });
};

export const recordRouteOptimization = (crew, oldRoute, newRoute, improvement) => {
  return scheduleHistory.recordChange({
    type: 'route_optimization',
    description: `Optimized route for ${crew.name}`,
    affectedDate: moment().format('YYYY-MM-DD'),
    before: {
      route: oldRoute,
      metrics: oldRoute.metrics
    },
    after: {
      route: newRoute,
      metrics: newRoute.metrics
    },
    metadata: {
      crewId: crew._id,
      milesSaved: improvement.milesSaved,
      timeSaved: improvement.timeSaved,
      impact: `Saved ${improvement.milesSaved} miles, ${improvement.timeSaved} minutes`
    }
  });
};

export const createDailySnapshot = (state) => {
  const date = moment().format('YYYY-MM-DD');
  return scheduleHistory.createRestorePoint(
    state,
    `Daily snapshot for ${date}`
  );
};